const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Concurrent 401s share one refresh call instead of stampeding the endpoint.
let inFlight = null;

/**
 * Trade the httpOnly refresh cookie for a new access token.
 * Returns the new token, or null if the session is genuinely over.
 */
export const refreshSession = async () => {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data?.token) return null;
      localStorage.setItem('advecti_token', data.token);
      if (data.advertiser) {
        localStorage.setItem('advecti_advertiser', JSON.stringify(data.advertiser));
      }
      return data.token;
    } catch {
      return null;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
};

export const clearSession = async () => {
  try {
    await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
  } catch {
    /* revoking server-side is best effort; the local token still goes */
  }
  localStorage.removeItem('advecti_token');
  localStorage.removeItem('advecti_advertiser');
};

/**
 * Renew proactively when the stored access token is missing, unreadable, or within
 * five minutes of expiry. Called before the dashboard's first data fetch so a stale
 * tab never fires a wave of 401s.
 */
export const ensureFreshToken = async () => {
  const token = localStorage.getItem('advecti_token');
  if (!token) return refreshSession();
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    if (!exp || exp * 1000 - Date.now() < 5 * 60 * 1000) return refreshSession();
    return token;
  } catch {
    return refreshSession();
  }
};

/**
 * Renew a few minutes before the 2-hour access token lapses, so long sessions on the
 * dashboard never hit a 401 in the first place.
 */
export const startSessionKeepalive = () => {
  const id = setInterval(refreshSession, 90 * 60 * 1000);
  return () => clearInterval(id);
};
